import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatCard } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { VerificationDetailDeclineModalComponent } from '../verification-detail-decline-modal/verification-detail-decline-modal.component';
import { VerificationDetailQuestionModalComponent } from '../verification-detail-question-modal/verification-detail-question-modal.component';
import { BehaviorSubject } from 'rxjs';
import { UserContextProvider } from '../../../../../infrastructure/context/user-context-provider';
import { dateFormat } from '../../../../../utilities/DateUtility';
import { NotificationService } from '../../../../../utilities/PortalNotificationUtility';
import { Reservation } from '../../../../reservations/models/Reservation';
import { LegacyReservationStatus } from '../../../../reservations/reservation-detail/ReservationDetailConstants';
import { getLegacyReservationStatus } from '../../../../reservations/reservation-detail/ReservationHelper';
import { ReservationService } from '../../../../reservations/services/reservation.service';
import { GuestVerificationCheckType } from '../../../models/Guest';
import { GuestDetails } from '../../../models/GuestDetails';
import { GuestVerificationResult } from '../../../models/GuestVerificationResult';
import { GuestVerificationInfo } from '../../../models/verification-info/GuestVerificationInfo';
import { GuestService } from '../../../services/guest.service';
import { GuestVerificationResultBuilder } from '../../../shared/guest-verification-result-builder';
import { UpdateGuestVerificationResultsDialogComponent } from './components/update-guest-verification-results-dialog/update-guest-verification-results-dialog.component';
import { Logger } from '../../../../../infrastructure/logging/logger';
import { ConfirmReverifyGuestDialogComponent } from '../confirm-reverify-guest-dialog/confirm-reverify-guest-dialog.component';

@Component({
  selector: 'app-verification-details',
  templateUrl: './verification-details.component.html',
  styleUrls: ['./verification-details.component.scss'],
  standalone: true,
  imports: [
    CommonModule,  
    MatCard,
    MatIcon,
    MatButtonModule
  ]
})
export class VerificationDetailsComponent implements OnInit {

  @Input() guestDetails!: GuestDetails;
  @Input() availableCheckTypes!: BehaviorSubject<GuestVerificationCheckType[]>;
  @Output() refreshGuestDetails: EventEmitter<any> = new EventEmitter();

  guestVerificationCheckType = GuestVerificationCheckType;
  guestVerificationInfo!: GuestVerificationInfo;
  reservation?: Reservation;
  checksToDisplay: GuestVerificationCheckType[] = [];
  identityCheckLabel!: string;
  shouldDisplayNotesColumn = false;
  topRisksExpanded = false;
  topRecommendationsExpanded = false;

  dateFormat = dateFormat;

  constructor(
    private dialog: MatDialog,
    private guestService: GuestService,
    private guestVerificationResultBuilder: GuestVerificationResultBuilder,
    private notificationService: NotificationService,
    public userContextProvider: UserContextProvider,
    private reservationService: ReservationService,
    private logger: Logger
  ) { }

  ngOnInit() {
    this.availableCheckTypes.subscribe((availableCheckTypes: GuestVerificationCheckType[]) => {
      const guestVerificationInfo = this.guestVerificationResultBuilder.buildFromGuestDetails(this.guestDetails, availableCheckTypes);
      if (!guestVerificationInfo) {
        this.logger.handleError(`An error occured retrieving Guest screening result details for guestId: ${this.guestDetails.guest_id}`, `Could not retrieve Guest screening details`, `An error occured retrieving Guest screening result details for guestId: ${this.guestDetails.guest_id}`);

        return;
      }
      this.guestVerificationInfo = guestVerificationInfo;
      this.identityCheckLabel = this.determineIdentityCheckLabel();
      this.checksToDisplay = this.getCheckTypesToDisplay(this.guestVerificationInfo);
      this.shouldDisplayNotesColumn = [
        this.guestVerificationInfo.verificationResultCode,
        this.guestVerificationInfo.nationalCriminalCode,
        this.guestVerificationInfo.identityResultCode,
        this.guestVerificationInfo.watchlistResultCode,
        this.guestVerificationInfo.sexOffenderResultCode,
        this.guestVerificationInfo.badGuestResultCode,
        this.guestVerificationInfo.fraudResultCode
      ].some((check) => check?.note != null && check?.note != '');
    });

    this.getReservation();
  }

  async getReservation() {
    try {
      if (this.guestDetails.reservation_guest_details[0].reservation_id) {
        this.reservation = await this.reservationService.getReservationById(this.guestDetails.reservation_guest_details[0].reservation_id);
      }
    } catch (e) {
      this.logger.handleError(e, `An error occurred while retrieving Reservation`, `Error while retrieving Reservation for guestId: ${this.guestDetails.guest_id}`);
    }
  }

  determineIdentityCheckLabel(): string {
    return this.guestDetails.verification_result_details.some((guest: any) => guest.check_type === 'DOCUMENT')
      ? 'Document'
      : 'Identity';
  }

  shouldDisplayDeclineButton(): boolean {
    if (!this.reservation) {
      return false;
    }

    return getLegacyReservationStatus(this.reservation) == LegacyReservationStatus.UPCOMING;
  }

  getVerificationResultColor(result?: GuestVerificationResult) {
    if (result?.color && result?.text) {
      return result.color;
    }

    return 'white';
  }

  async onGuestVerificationPdfDownloadClick() {
    try {
      const reservationId = this.guestDetails?.reservation_guest_details[0]?.reservation_id;
      const guestId = this.guestDetails?.guest_id;
      await this.guestService.getGuestVerificationPdf(guestId, reservationId);
    } catch (e) {
      this.logger.handleError(e, `An error occured downloading Guest Screening PDF`, `Error downloading Guest Screening PDF for guestId: ${this.guestDetails.guest_id}`);
    }
  }

  async onReverifyGuestClick() {
    this.openConfirmReverifyGuestDialog();
  }

  openConfirmReverifyGuestDialog() {
    const verifyDialogRef = this.dialog.open(ConfirmReverifyGuestDialogComponent, { 
      width: '55%' 
    });
    verifyDialogRef.afterClosed().subscribe(async confirm => {
      if (confirm) {
        try {
          const payload = {
            guest_id: this.guestDetails?.guest_id
          }
          await this.guestService.reverifyGuest(payload).then(_ => {
            this.notificationService.showNotification({ message: 'Reverify Guest submitted' });
            this.refreshGuestDetails.emit();
          });
        } catch (e) {
          this.logger.handleError(e, `An error occured reverifying Guest`, `Error reverifying Guest for guestId: ${this.guestDetails.guest_id}`);
        }
      }
    });
  }

  onQuestionClick(): void {
    const dialogRef = this.dialog.open(VerificationDetailQuestionModalComponent, { 
      width: '55%' 
    });
    dialogRef.afterClosed().subscribe(async question => {
      if (question != null) {
        await this.guestService.sendQuestion(this.guestVerificationInfo.id, question);
        this.notificationService.showNotification({ message: 'Question sent' });
      }
    });
  }

  onDeclineClick(): void {
    const dialogRef = this.dialog.open(VerificationDetailDeclineModalComponent, {
      width: '800px'
    });
    dialogRef.afterClosed().subscribe(async reason => {
      if (reason != null) { // use !== if we want to allow blank decline messages
        await this.guestService.sendDecline(this.guestVerificationInfo.id, reason);
        this.notificationService.showNotification({ message: 'Guest decline request sent' });
      }
    });
  }

  getCheckTypesToDisplay(guestVerificationInfo: GuestVerificationInfo): GuestVerificationCheckType[] {
    const checkTypes = [
      GuestVerificationCheckType.IDENTITY,
      GuestVerificationCheckType.DOCUMENT,
      GuestVerificationCheckType.WATCHLIST,
      GuestVerificationCheckType.SEX_OFFENDER,
      GuestVerificationCheckType.BACKGROUND,
      GuestVerificationCheckType.BAD_GUEST,
      GuestVerificationCheckType.FRAUD
    ];

    return checkTypes.filter(checkType => this.shouldDisplayCheckType(checkType, guestVerificationInfo));
  }

  shouldDisplayCheckType(checkType: GuestVerificationCheckType, guestVerificationInfo: GuestVerificationInfo): boolean {
    if(!this.availableCheckTypes.value.includes(checkType)) {
      return false;
    }

    const isNaUser = this.userContextProvider.isNaUser();
    switch(checkType) {
      case GuestVerificationCheckType.SEX_OFFENDER:
        return guestVerificationInfo.sexOffenderResultCode != null && isNaUser;
      case GuestVerificationCheckType.BACKGROUND:
        return guestVerificationInfo.nationalCriminalCode != null && isNaUser;
      case GuestVerificationCheckType.BAD_GUEST:
        return guestVerificationInfo.badGuestResultCode != null && isNaUser;
      case GuestVerificationCheckType.FRAUD:
        return guestVerificationInfo.fraudResultCode != null && isNaUser;
      case GuestVerificationCheckType.WATCHLIST:
        return guestVerificationInfo.watchlistResultCode != null;
      case GuestVerificationCheckType.IDENTITY:
      case GuestVerificationCheckType.DOCUMENT:
        return guestVerificationInfo.identityResultCode != null;
      default:
        return false;
    }
  }

  shouldDisplayScreeningResultColumns(): boolean {
    return this.availableCheckTypes != null && this.availableCheckTypes.value.length > 0;
  }

  getCheckLabel(checkType: GuestVerificationCheckType) {
    switch(checkType) {
      case GuestVerificationCheckType.IDENTITY:
      case GuestVerificationCheckType.DOCUMENT:
        return 'Document';
      case GuestVerificationCheckType.WATCHLIST:
        return 'Watchlist';
      case GuestVerificationCheckType.SEX_OFFENDER:
        return 'U.S Sex Offender List';
      case GuestVerificationCheckType.BACKGROUND:
        return 'U.S Criminal History';
      case GuestVerificationCheckType.BAD_GUEST:
        return 'Safely Prior Rental Database';
      case GuestVerificationCheckType.FRAUD:
        return 'Suspicious Behavior Check';
      default:
        return undefined;
    }
}

  getResultCode(checkType: GuestVerificationCheckType): GuestVerificationResult | undefined {
    switch(checkType) {
      case GuestVerificationCheckType.IDENTITY:
      case GuestVerificationCheckType.DOCUMENT:
        return this.guestVerificationInfo.identityResultCode;
      case GuestVerificationCheckType.WATCHLIST:
          return this.guestVerificationInfo.watchlistResultCode;
      case GuestVerificationCheckType.SEX_OFFENDER:
        return this.guestVerificationInfo.sexOffenderResultCode;
      case GuestVerificationCheckType.BACKGROUND:
        return this.guestVerificationInfo.nationalCriminalCode;
      case GuestVerificationCheckType.BAD_GUEST:
        return this.guestVerificationInfo.badGuestResultCode;
      case GuestVerificationCheckType.FRAUD:
        return this.guestVerificationInfo.fraudResultCode;
      default:
        return undefined;
    }
  }

  getResultText(checkType: GuestVerificationCheckType): string | undefined {
    return this.getResultCode(checkType)?.text?.toLowerCase();
  }

  getRecommendation(checkType: GuestVerificationCheckType): string {
    return this.getResultCode(checkType)?.recommendation ?? 'None given';
  }

  getNote(checkType: GuestVerificationCheckType): string {
    return this.getResultCode(checkType)?.note ?? 'None given';
  }

  getRiskLabel(riskColor: string): string {
    switch (riskColor?.toLowerCase()) {
      case 'red': return 'High Risk';
      case 'yellow': return 'Medium Risk';
      case 'green': return 'Low Risk';
      default: return riskColor;
    }
  }

  onUpdateGuestVerificationResultsClick() {
    const updateDialogRef = this.dialog.open(UpdateGuestVerificationResultsDialogComponent, { 
      width: '90vw',
      disableClose: false,
      autoFocus: false,
      data: {
        guestDetails: this.guestDetails,
        guestVerificationInfo: this.guestVerificationInfo,
        availableCheckTypes: this.availableCheckTypes
      }
    });
    updateDialogRef.afterClosed().subscribe(updatedVerifResults => {
      if (updatedVerifResults) {
        this.guestService.updateGuestVerificationResultDetail(updatedVerifResults).then(result => {
          if (result.err) {
            this.notificationService.showNotification({ message: result.err.msg });
          } else {
            this.notificationService.showNotification({ message: 'Guest Verification Results updated' });
            this.refreshGuestDetails.emit();
          }
        });
      }
    });
  }
}